import React, { FC, useEffect, useRef, useState } from "react";
import { Spinner } from "src/components/common";
import {
	IUserTrainingCourseSummary,
	IUserTrainingCourseVideoSummary,
	Results,
	SyncVideoRequest,
	TrainingQuizResultRequest,
	TrainingSyncSource,
} from "src/@types/ImpiricaPlatformApi";
import { useApi } from "src/Api/useApi";
import classNames from "classnames";
import { ClientTrainingTOC } from "./ClientTrainingTOC";
import { SpotlightrGallery } from "./SpotlightrGallery/SpotlightrGallery";
import styles from "./ClientCoursePage.module.css";
import SpotlightrVideoModal from "./SpotlightrGallery/SpotlightrVideoModal";
import SpotlightrQuizModal from "./SpotlightrGallery/SpotlightrQuizModal";
import { toast } from "react-toastify";
import { Alert } from "antd";

declare global {
	interface Window {
		spotlightrAPI: any;
	}
}

/** Head start for the player's save to Spotlightr (sendVideoAnalytics on end, or the iframe unloading on close). */
const SYNC_DELAY_MS = 3_000;
/** One more look, measured from the first sync, for a save that landed late. */
const SYNC_RETRY_MS = 7_000;

interface ClientCoursePageProps {
	history?: any;
	location?: any;
	match?: any;
	children?: any;
}

const ClientCoursePage: FC<ClientCoursePageProps> = ({ history, match }) => {
	const [loading, setLoading] = useState<boolean>(true);
	const [course, setCourse] = useState<IUserTrainingCourseSummary>();
	const [videoPopupDetails, setVideoPopupDetails] =
		useState<IUserTrainingCourseVideoSummary>(); // [videoId, videoName
	const [quizPopupDetails, setQuizPopupDetails] =
		useState<IUserTrainingCourseSummary>(); // [quizId
	const { clientTrainingClient } = useApi();

	const _refreshTOC = async () => {
		if (!clientTrainingClient) throw new Error("Api is not available");
		if (!course) throw new Error("Course is not available");
		const { courseId } = course;

		try {
			const updated = await clientTrainingClient.getTrainingCourse(courseId);
			_applyCourse(updated);
		} catch (e) {}
	};

	// Closing the video, closing the quiz and saving a quiz score can overlap. Running their requests one at a
	// time means each sees the previous one's writes (no double completion) and responses land in the order sent.
	const syncQueue = useRef<Promise<void>>(Promise.resolve());
	const _enqueue = (run: () => Promise<void>) => {
		syncQueue.current = syncQueue.current.then(run);
		return syncQueue.current;
	};

	// queued work runs before React re-renders, so it reads the latest course from here rather than a stale closure
	const latestCourse = useRef<IUserTrainingCourseSummary>();
	const _applyCourse = (updated: IUserTrainingCourseSummary) => {
		latestCourse.current = updated;
		setCourse(updated);
	};

	const _isVideoComplete = (summary: IUserTrainingCourseSummary | undefined, videoId: string) =>
		!!summary?.sections?.some((s) => s.videos?.some((v) => v.id === videoId && v.complete));

	/**
	 * Resolves with the course after the sync, or undefined when the sync failed (the learner has been warned).
	 * A video the course already shows complete is not synced again: completion never goes back, so the call
	 * could learn nothing - this is what stops the end sync and the close sync doubling up.
	 */
	const _syncVideo = (videoId: string, source: TrainingSyncSource) => {
		if (!clientTrainingClient) throw new Error("Api is not available");
		if (!course) throw new Error("Course is not available");
		const { courseId } = course;

		let synced: IUserTrainingCourseSummary | undefined;
		return _enqueue(async () => {
			if (_isVideoComplete(latestCourse.current, videoId)) {
				synced = latestCourse.current;
				return;
			}
			try {
				const updated = await clientTrainingClient.syncVideo(
					courseId,
					videoId,
					new SyncVideoRequest({ source })
				);
				// the sync returns the recomputed course, so no separate TOC refresh is needed here
				if (updated) _applyCourse(updated);
				synced = updated;
			} catch (e) {
				// usually Spotlightr being down, not the learner's fault; nudge a retry rather than alarm them
				toast.warning("Couldn't save your video progress right now. Reopen the video to try again.");
			}
		}).then(() => synced);
	};

	// Spotlightr only has the watch once the player has saved it, and the save is fire-and-forget. Give it a head
	// start, then retry once in case it landed late. Timers are not cleared on unmount: leaving the page right
	// after closing must still record the watch.
	const _syncAfterSave = (videoId: string, source: TrainingSyncSource) => {
		setTimeout(async () => {
			const synced = await _syncVideo(videoId, source);
			if (synced && !_isVideoComplete(synced, videoId)) {
				setTimeout(() => _syncVideo(videoId, source), SYNC_RETRY_MS);
			}
		}, SYNC_DELAY_MS);
	};

	// the modal has already asked the player to save the view (sendVideoAnalytics)
	const _onVideoEnded = (videoId: string) => _syncAfterSave(videoId, TrainingSyncSource.PlayerEvent);

	// closing unloads the player iframe, which saves the view: the fallback for a learner who stops part way
	const _onCloseVideo = (videoId: string) => {
		setVideoPopupDetails(undefined);
		_syncAfterSave(videoId, TrainingSyncSource.ModalClose);
	};

	const _onCloseQuiz = () => {
		setQuizPopupDetails(undefined);
		// queued behind any score save still in flight. A passing score already redrew the course from the
		// addTrainingCourseScore response; only an abandoned / failed attempt needs a refetch
		return _enqueue(async () => {
			if (!latestCourse.current?.quizComplete) await _refreshTOC();
		});
	};

	const _onQuizComplete = async (passed: boolean, results: unknown) => {
		if (!clientTrainingClient) throw new Error("Api is not available");
		if (!course) throw new Error("Quiz is not available");
		if (!course.quizId) throw new Error("Quiz is not available");
		if (!passed) return;

		const { courseId, quizId } = course;

		await _enqueue(async () => {
			try {
				const updated = await clientTrainingClient.addTrainingCourseScore(
					courseId,
					quizId,
					new TrainingQuizResultRequest({ passed, results: results as Results })
				);
				if (updated) _applyCourse(updated);
			} catch (e) {
				toast.error("Unable to save quiz result!");
			}
		});
	};

	const _onLaunchVideo = (video: IUserTrainingCourseVideoSummary) => {
		setVideoPopupDetails(video);
	};

	const _onLaunchQuiz = () => {
		setQuizPopupDetails(course);
	};

	useEffect(() => {
		if (!clientTrainingClient) return;

		_loadTrainingCourse(match.params.courseId);
	}, [clientTrainingClient]);

	const _loadTrainingCourse = async (courseId: number) => {
		if (!clientTrainingClient) throw new Error("Api is not available");
		try {
			setLoading(true);
			const trainingCourse = await clientTrainingClient.getTrainingCourse(
				courseId
			);
			_applyCourse(trainingCourse);
		} finally {
			setLoading(false);
		}
	};

	const _backToCourses = async () => {
		history.push({
			pathname: "/client-training",
		});
	};

	if (loading || course === undefined) {
		return (
			<div className={styles.loading}>
				<Spinner className={"spinner-large"} />
			</div>
		);
	}

	return (
		<div className={styles.outerWrapper}>
			{course.courseComplete && (
				<Alert message="Course complete" type="success" showIcon />
			)}

			<div className={styles.clientCoursePage}>
				<div className={classNames("bg-light", styles.courseContentsWrapper)}>
					<div className={styles.scrollContainer}>
						<div className={styles.courseContents}>
							<ClientTrainingTOC
								key={course.courseId}
								course={course}
								onLaunchQuiz={_onLaunchQuiz}
							/>
						</div>

						<div className={styles.backToCourses}>
							<button
								className={styles.backToCoursesButton}
								type="button"
								onClick={_backToCourses}
							>
								Back to courses
							</button>
						</div>
					</div>
				</div>

				<div className={styles.contentWrapper}>
					<div className={styles.scrollContainer}>
						<div className={classNames(styles.contentLayout)}>
							<SpotlightrGallery
								course={course}
								onLaunchVideo={_onLaunchVideo}
							/>
						</div>
					</div>
				</div>

				{videoPopupDetails && (
					<SpotlightrVideoModal
						video={videoPopupDetails}
						onClose={_onCloseVideo}
						onEnded={_onVideoEnded}
					/>
				)}

				{quizPopupDetails && (
					<SpotlightrQuizModal
						course={quizPopupDetails}
						onClose={_onCloseQuiz}
						onComplete={_onQuizComplete}
					/>
				)}
			</div>
		</div>
	);
};

export default ClientCoursePage;
